import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  UserCircle,
  Mail,
  Shield,
  FileDown,
  Trash2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { updateProfileSchema, type UpdateProfileInput } from '@/shared/validation/auth';
import { api } from '@/client/lib/api';
import { useMe, type Me } from '@/client/hooks/useMe';
import { Alert } from '@/client/components/ui/alert';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Checkbox } from '@/client/components/ui/checkbox';
import { isLocale, type Locale } from '@/client/i18n';

export function Profile() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const [saved, setSaved] = useState(false);

  const form = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: { name: me?.name ?? '', marketing_consent: me?.marketingConsent ?? false },
  });

  useEffect(() => {
    if (me) {
      form.reset({ name: me.name, marketing_consent: me.marketingConsent });
    }
  }, [me, form]);

  if (!me) return null;

  async function onSubmit(values: UpdateProfileInput) {
    setSaved(false);
    const data = await api<{ user: Me }>('/api/v1/me', { method: 'PATCH', body: values });
    queryClient.setQueryData(['me'], data.user);
    setSaved(true);
  }

  async function handleExport() {
    const response = await fetch('/api/v1/me/export', { credentials: 'same-origin' });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dr-akademia-export.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function handleDelete() {
    if (!window.confirm(t('portal.profile_delete_confirm'))) return;
    await api('/api/v1/me', { method: 'DELETE' });
    queryClient.setQueryData(['me'], null);
    navigate(`/${locale}/`);
  }

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Personal info section */}
      <section className="dr-card-static relative overflow-hidden p-6 sm:p-8">
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(193,167,226,0.28) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary shrink-0">
              <UserCircle size={22} />
            </div>
            <div>
              <p className="dr-eyebrow">
                {t('portal.profile_eyebrow', { defaultValue: 'Profil' })}
              </p>
              <h2 className="mt-1 font-display text-h3 text-primary-dark">
                {t('portal.profile_title')}
              </h2>
            </div>
          </div>

          {saved && (
            <Alert variant="success" className="mt-5">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={14} />
                {t('portal.profile_saved')}
              </span>
            </Alert>
          )}

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-6 space-y-5 max-w-lg"
            noValidate
          >
            <div>
              <Label htmlFor="name">{t('auth.name_label')}</Label>
              <Input id="name" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="mt-1.5 font-sans text-caption text-error">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div>
              <Label>{t('auth.email_label')}</Label>
              <div className="relative">
                <Input value={me.email} disabled className="pr-10" />
                <Mail
                  size={16}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted"
                />
              </div>
            </div>
            <label className="flex items-start gap-3 rounded-2xl bg-white/55 backdrop-blur-md border border-glass-border p-4 cursor-pointer hover:bg-white/70 transition-colors">
              <Checkbox className="mt-0.5" {...form.register('marketing_consent')} />
              <span className="font-sans text-body-small text-text-secondary leading-relaxed">
                {t('portal.profile_marketing_label')}
              </span>
            </label>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? t('common.loading') : t('portal.profile_save')}
            </Button>
          </form>
        </div>
      </section>

      {/* Data export section */}
      <section className="dr-card-static p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary shrink-0">
            <Shield size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="dr-eyebrow">
              {t('portal.profile_export_eyebrow', { defaultValue: 'Tvoje dáta' })}
            </p>
            <h2 className="mt-1 font-display text-h3 text-primary-dark">
              {t('portal.profile_export_title')}
            </h2>
            <p className="mt-2 font-sans text-body text-text-secondary max-w-2xl leading-relaxed">
              {t('portal.profile_export_body')}
            </p>
            <Button variant="secondary" className="mt-5" onClick={handleExport}>
              <FileDown size={16} />
              {t('portal.profile_export_button')}
            </Button>
          </div>
        </div>
      </section>

      {/* Danger zone */}
      <section className="rounded-2xl bg-white/55 backdrop-blur-md border border-error/25 shadow-glass-soft p-6 sm:p-8 relative overflow-hidden">
        <div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(196,50,45,0.10) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative flex items-start gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-error/10 border border-error/20 text-error shrink-0">
            <AlertTriangle size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-sans text-caption font-semibold uppercase tracking-[0.18em] text-error">
              {t('portal.profile_delete_eyebrow', { defaultValue: 'Riziková zóna' })}
            </p>
            <h2 className="mt-1 font-display text-h3 text-error">
              {t('portal.profile_delete_title')}
            </h2>
            <p className="mt-2 font-sans text-body text-text-secondary max-w-2xl leading-relaxed">
              {t('portal.profile_delete_body')}
            </p>
            <Button variant="danger" className="mt-5" onClick={handleDelete}>
              <Trash2 size={16} />
              {t('portal.profile_delete_button')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
